import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Space, Tag, Card, Typography, Row, Col, Upload, Avatar, Select, Drawer, Badge, InputNumber, DatePicker, Switch } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined, 
  UploadOutlined, 
  CameraOutlined, 
  UserOutlined, 
  CheckOutlined, 
  CloseOutlined, 
  EyeOutlined,
  DashboardOutlined,
  FileTextOutlined,
  TeamOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuOutlined,
  HomeOutlined,
  HeartOutlined,
  CrownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../../services/api';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import PanelLegalLinks from '../../../components/Common/PanelLegalLinks';
import DashboardTab from './DashboardTab';
import CompanyProfileTab from './CompanyProfileTab';
import JobPostingsTab from './JobPostingsTab';
import ApplicationsTab from './ApplicationsTab';
import CandidateSearchTab from './CandidateSearchTab';
import FavoritesTab from './FavoritesTab';
import PoolTab from './PoolTab';
import SettingsTab from './SettingsTab';
import SubscriptionTab from './SubscriptionTab';
import SubscriptionProtectedRoute from '../../../components/Common/SubscriptionProtectedRoute';
import { CompanyProfileResponse } from '../../../types';
import styles from './CompanyPanel.module.css';

const { Title } = Typography;
const { Option } = Select;

const CompanyPanel: React.FC = () => {
  const toast = useToast();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const { data: profileData } = useQuery({
    queryKey: companyProfileQueryKeys.profile,
    queryFn: async () => {
      const response = await apiService.getCompanyProfile();
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      const validTabs = ['dashboard', 'profile', 'subscription', 'job-postings', 'candidate-search', 'applications', 'favorites', 'pool', 'settings'];
      if (validTabs.includes(tab)) {
        setSelectedMenu(tab);
        if (tab !== 'settings') {
          const next = new URLSearchParams(searchParams);
          next.delete('tab');
          setSearchParams(next, { replace: true });
        }
      }
    }
  }, [searchParams, setSearchParams]);

  const menuItems = [
    { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
    { key: 'profile', label: 'Profil Bilgileri', icon: <UserOutlined /> },
    { key: 'subscription', label: 'Abonelik Bilgileri', icon: <CrownOutlined /> },
    { key: 'job-postings', label: 'İş İlanları', icon: <FileTextOutlined /> },
    { key: 'candidate-search', label: 'Aday Ara', icon: <SearchOutlined /> },
    { key: 'applications', label: 'Başvurular', icon: <TeamOutlined /> },
    { key: 'favorites', label: 'Favoriler', icon: <HeartOutlined /> },
    { key: 'pool', label: 'Aday Havuzu', icon: <TeamOutlined /> },
    { key: 'settings', label: 'Ayarlar', icon: <SettingOutlined /> }
  ];

  const handleMenuClick = (key: string) => {
    setSelectedMenu(key);
    setMobileMenuVisible(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderSidebar = () => (
    <>
      <div className="panel-sidebar-brand">
        <Avatar
          size={48}
          src={profileData?.logo ? `data:image/jpeg;base64,${profileData.logo}` : undefined}
          icon={<UserOutlined />}
          style={{ backgroundColor: 'var(--panel-accent)' }}
        >
          {!profileData?.logo && profileData?.companyName ? profileData.companyName.charAt(0).toUpperCase() : 'Ş'}
        </Avatar>
        <h2 className="panel-user-name" style={{ marginTop: 12, marginBottom: 2 }}>{profileData?.companyName || 'Şirket'}</h2>
        <span className="panel-user-role">Şirket Paneli</span>
        {(profileData?.email || user?.email) && (
          <span className="panel-user-email">{profileData?.email || user?.email}</span>
        )}
      </div>
      <nav className="panel-nav">
        {menuItems.map(item => (
          <button
            type="button"
            key={item.key}
            className={`panel-nav-item ${selectedMenu === item.key ? 'panel-nav-item--active' : ''}`}
            onClick={() => handleMenuClick(item.key)}
          >
            <span className="panel-nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="panel-sidebar-footer">
        <PanelLegalLinks />
        <button type="button" className="panel-logout" onClick={handleLogout}>
          <LogoutOutlined /> Çıkış Yap
        </button>
      </div>
    </>
  );

  const renderContent = () => {
    switch (selectedMenu) {
      case 'dashboard':
        return <DashboardTab />;
      case 'profile':
        return (
          <SubscriptionProtectedRoute allowUnsubscribed={true}>
            <CompanyProfileTab />
          </SubscriptionProtectedRoute>
        );
      case 'subscription':
        return (
          <SubscriptionProtectedRoute allowUnsubscribed={true}>
            <SubscriptionTab />
          </SubscriptionProtectedRoute>
        );
      case 'job-postings':
        return (
          <SubscriptionProtectedRoute>
            <JobPostingsTab />
          </SubscriptionProtectedRoute>
        );
      case 'candidate-search':
        return (
          <SubscriptionProtectedRoute>
            <CandidateSearchTab />
          </SubscriptionProtectedRoute>
        );
      case 'applications':
        return (
          <SubscriptionProtectedRoute>
            <ApplicationsTab />
          </SubscriptionProtectedRoute>
        );
      case 'favorites':
        return (
          <SubscriptionProtectedRoute>
            <FavoritesTab />
          </SubscriptionProtectedRoute>
        );
      case 'pool':
        return (
          <SubscriptionProtectedRoute>
            <PoolTab />
          </SubscriptionProtectedRoute>
        );
      case 'settings':
        return (
          <SubscriptionProtectedRoute>
            <SettingsTab />
          </SubscriptionProtectedRoute>
        );
      default:
        return <DashboardTab />;
    }
  };

  return (
    <div className={styles.container} style={{ '--panel-accent': '#2563eb' } as React.CSSProperties}>
      <aside className="panel-sidebar">
        {renderSidebar()}
      </aside>

      <Drawer
        title="Menü"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={260}
        className={styles.mobileDrawer}
        styles={{ header: { borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#f8fafc', background: '#1e293b' }, body: { padding: 0, background: '#1e293b' } }}
      >
        <div className="panel-sidebar" style={{ position: 'relative', width: '100%', boxShadow: 'none' }}>
          {renderSidebar()}
        </div>
      </Drawer>

      <div className="panel-content">
        <header className="panel-header">
          <div className="panel-header-left">
            <Button type="primary" icon={<MenuOutlined />} onClick={() => setMobileMenuVisible(true)} className={styles.mobileMenuButton} aria-label="Menüyü aç">
              <span className={styles.mobileMenuButtonText}>Menü</span>
            </Button>
            <Button type="text" icon={<HomeOutlined />} onClick={() => navigate('/')} className={styles.homeButton}>
              Anasayfa
            </Button>
          </div>
          <div className="panel-header-right">
            <span className={styles.welcomeText}>Hoş geldiniz, {user?.name || 'Kullanıcı'}</span>
          </div>
        </header>
        <main className="panel-main">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default CompanyPanel;